import React, { useState, useRef, useEffect } from "react";
import { Link } from "gatsby";
import styled from "styled-components";
import { PrismicRichText } from "@prismicio/react";
import { motion, useScroll, useTransform } from "framer-motion";
import { GatsbyImage } from "gatsby-plugin-image";
import { FormatDate } from "../../components/utils/format-date";

const ScrollWrapper = styled.div`
  height: 300vh;
  width: 100%;
`;

const Page = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 100px 30px 50px 30px;
  overflow: hidden;

  & .content-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 17vw;
    padding: 0;
    height: 100%;
    padding-right: 60vw;
    padding-left: 25vw;
  }

  & .exhibition-item {
    min-width: 50vw;
    max-width: 50vw;
    max-height: 60vh;
    display: flex;
    flex-direction: column;
    aspect-ratio: 4/3;

    & .gatsby-image-wrapper {
      height: 100%;
      max-height: calc(60svh - 62px);
    }

    & .title-container {
      margin-top: 20px;

      & p {
        margin: 0;
      }
    }
  }

  & .year-label {
    width: 100px;
    height: calc(60svh - 20px);
    display: flex;
    justify-content: flex-start;
    align-items: flex-end;
  }

  @media (max-width: 860px) {
    padding: 120px 20px 20px 20px;
  }
`;

const DesktopArchive = ({ items }) => {
  const containerRef = useRef(null);
  const [scrollHeight, setScrollHeight] = useState(300);
  const [transformValue, setTransformValue] = useState("0%");

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Group items by year
  const groupByYear = () => {
    const grouped = {};

    items.forEach((item) => {
      // Extract year from start_date
      const year = new Date(item.data.start_date).getFullYear();
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(item);
    });

    // Sort years in descending order
    return Object.keys(grouped)
      .sort((a, b) => b - a)
      .map((year) => ({
        year,
        items: grouped[year],
      }));
  };

  const groupedItems = groupByYear();

  useEffect(() => {
    if (typeof window !== "undefined") {
      // Calculate total items: original items + one year label per group
      const totalItems = items.length + groupedItems.length;
      // Each item is 60vw wide + 5vw gap
      const totalVW = totalItems * (60 + 5);
      // Calculate how many viewport widths we need
      const viewportsNeeded = totalVW / 100;
      // Convert to viewport heights - multiply by 1.5 to ensure enough scroll space
      const newScrollHeight = Math.ceil(viewportsNeeded * 150);

      setScrollHeight(newScrollHeight);
      setTransformValue(`${-totalVW}vw`);
    }
  }, [items.length, groupedItems.length]);

  const x = useTransform(scrollYProgress, [0, 1], ["0%", transformValue]);

  let contentIndex = 0;
  const content = groupedItems.flatMap((group) => {
    const yearLabel = (
      <motion.div
        key={`year_${group.year}`}
        className="year-label"
        initial={{ opacity: 0, x: 100 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: contentIndex * 0.1 }}
      >
        <h1>{group.year}</h1>
      </motion.div>
    );

    contentIndex++;

    const yearItems = group.items.map((item) => {
      const element = (
        <motion.div
          key={`single_archived_exhibition_${contentIndex}_${item.id}`}
          className="exhibition-item"
          initial={{ opacity: 0, x: 100 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: contentIndex * 0.1 }}
        >
          <Link to={item.url}>
            <GatsbyImage
              image={item.data.desktop_thumbnail.gatsbyImageData}
              alt={item.data.desktop_thumbnail.alt}
            />

            <div className="title-container">
              <h1>
                <PrismicRichText
                  field={item.data.artist.richText}
                  components={{
                    heading2: ({ children }) => (
                      <span className="">{children}, </span>
                    ),
                  }}
                />

                <PrismicRichText
                  field={item.data.title.richText}
                  components={{
                    heading1: ({ children }) => (
                      <span className="italic">{children}</span>
                    ),
                  }}
                />
              </h1>

              <PrismicRichText field={item.data.additional_info.richText} />

              <p>{FormatDate(item.data.start_date, item.data.end_date)}</p>
            </div>
          </Link>
        </motion.div>
      );

      contentIndex++;
      return element;
    });

    return [yearLabel, ...yearItems];
  });

  return (
    <ScrollWrapper ref={containerRef} style={{ height: `${scrollHeight}vh` }}>
      <Page>
        <motion.div className="content-container" style={{ x }}>
          {content}
        </motion.div>
      </Page>
    </ScrollWrapper>
  );
};

export default DesktopArchive;
